// ============================================================
// ASSESSMENT QUALITY GATE + ACTIVITY MONITORING ENGINE
// ============================================================

import { requireFinitePositiveNumber, coerceToString } from '../validate.js';
import { businessDate as _defaultBusinessDate } from './business-date.js';
import { SANCTIONED_COUNTRIES, HIGH_RISK_COUNTRIES, HIGH_RISK_SIC_CODES, getCountryName, COUNTRY_ALIASES } from '../utils/country-codes.js';

let _seq = 0;
function genId(pfx = '') { _seq++; return `${pfx}${Date.now().toString(36)}${_seq.toString(36).padStart(4, '0')}${Math.random().toString(36).slice(2, 6)}`; }

// ============================================================
// LAYER 1: ASSESSMENT QUALITY GATE
// ============================================================

const QualitySeverity = { INFO: 'INFO', WARNING: 'WARNING', CRITICAL: 'CRITICAL' };

const QualityChecks = {
  businessUnderstandingLength: (a) => {
    const t = coerceToString(a.businessUnderstanding).trim();
    if (!t || t.length < 10) return { pass: false, warning: 'Business understanding is missing or trivially short', field: 'businessUnderstanding', severity: QualitySeverity.CRITICAL };
    if (t.length < 50) return { pass: false, warning: 'Business understanding should describe what the business does, its industry, and scale', field: 'businessUnderstanding', severity: QualitySeverity.WARNING };
    return { pass: true };
  },
  businessUnderstandingNotTrivial: (a, ctx) => {
    const t = coerceToString(a.businessUnderstanding).toLowerCase().trim();
    const n = coerceToString(ctx?.customerName).toLowerCase().trim();
    if (n && t && (t === n || t === `${n} business` || t === 'see above')) return { pass: false, warning: 'Must describe the actual business, not just restate the company name', field: 'businessUnderstanding', severity: QualitySeverity.WARNING };
    return { pass: true };
  },
  sourceOfWealthPresent: (a) => {
    const t = coerceToString(a.sourceOfWealth).trim();
    if (!t || t.length < 5) return { pass: false, warning: 'Source of wealth is required', field: 'sourceOfWealth', severity: QualitySeverity.CRITICAL };
    const trivial = ['n/a', 'na', 'none', 'business', 'income', 'money', 'unknown', '-', '.', 'tbc', 'tbd'];
    if (trivial.includes(t.toLowerCase())) return { pass: false, warning: 'Source of wealth must provide meaningful detail', field: 'sourceOfWealth', severity: QualitySeverity.WARNING };
    return { pass: true };
  },
  expectedActivityQuantified: (a) => {
    const t = coerceToString(a.expectedActivity).trim();
    if (!t || t.length < 5) return { pass: false, warning: 'Expected account activity is required', field: 'expectedActivity', severity: QualitySeverity.CRITICAL };
    const hasNumbers = /\d/.test(t);
    const hasCurrency = /[£$€]/.test(t);
    const hasQuantifiers = /(monthly|annually|per month|per year|per quarter|weekly|daily|turnover|volume)/i.test(t);
    if (!hasNumbers && !hasCurrency) return { pass: false, warning: 'Expected activity should include quantified values', field: 'expectedActivity', severity: QualitySeverity.WARNING };
    if (!hasQuantifiers) return { pass: false, warning: 'Expected activity should specify frequency', field: 'expectedActivity', severity: QualitySeverity.INFO };
    return { pass: true };
  },
  highRiskConcernsPresent: (a, ctx) => {
    if (ctx?.riskRating !== 'HIGH' && ctx?.riskRating !== 'PROHIBITED') return { pass: true };
    const t = coerceToString(a.concerns).trim();
    if (!t || t.length < 10 || /^(none|n\/a|no|nil)$/i.test(t)) return { pass: false, warning: 'HIGH risk: concerns must document specific risk factors', field: 'concerns', severity: QualitySeverity.CRITICAL };
    return { pass: true };
  },
  mitigantsWhenConcerns: (a) => {
    const c = coerceToString(a.concerns).trim();
    const m = coerceToString(a.mitigants).trim();
    const trivialConcerns = ['none', 'none identified', 'no concerns', 'n/a', 'na', 'nil', '-', 'not applicable', 'no', 'nothing'];
    const hasConcerns = c.length > 0 && !trivialConcerns.includes(c.toLowerCase());
    if (hasConcerns && (!m || m.length < 10 || /^(none|n\/a|no|nil)$/i.test(m))) return { pass: false, warning: 'Concerns documented but mitigants missing', field: 'mitigants', severity: QualitySeverity.WARNING };
    return { pass: true };
  },
  screeningMatchesAddressed: (a, ctx) => {
    if (!ctx?.hasScreeningMatches) return { pass: true };
    const combined = (coerceToString(a.concerns) + ' ' + coerceToString(a.mitigants)).toLowerCase();
    if (!/(screening|pep|sanction|match|flag|hit|alert|adverse)/i.test(combined)) return { pass: false, warning: 'Screening matches not addressed in concerns/mitigants', field: 'concerns', severity: QualitySeverity.CRITICAL };
    return { pass: true };
  },
  prohibitedApproveContradiction: (a) => {
    if (a.recommendation === 'APPROVE' && a.riskRating === 'PROHIBITED') return { pass: false, warning: 'Cannot approve PROHIBITED risk — system will override to REJECT', field: 'recommendation', severity: QualitySeverity.CRITICAL };
    return { pass: true };
  },
  contentQuality: (a) => {
    const vowels = /[aeiouy]/i;
    const consecutiveConsonants = /[^aeiouy\s\d£$€.,;:!?'"()\-/]{5,}/i;
    function isRealWord(w) {
      return w.length >= 1 && w.length <= 20 && vowels.test(w) && !consecutiveConsonants.test(w);
    }
    const fields = ['businessUnderstanding', 'sourceOfWealth', 'expectedActivity'];
    for (const field of fields) {
      const t = coerceToString(a[field]).trim();
      if (!t) continue;
      const words = t.split(/\s+/).filter(w => w.length > 0);
      if (words.length === 0) continue;
      const realCount = words.filter(isRealWord).length;
      if (realCount / words.length < 0.6) return { pass: false, warning: 'Assessment text appears to contain nonsensical or random content', field, severity: QualitySeverity.CRITICAL };
    }
    return { pass: true };
  },
  minimumWordCount: (a) => {
    const buWords = coerceToString(a.businessUnderstanding).trim().split(/\s+/).filter(w => w.length > 0);
    if (buWords.length > 0 && buWords.length < 8) return { pass: false, warning: 'Business understanding must contain at least 8 words', field: 'businessUnderstanding', severity: QualitySeverity.CRITICAL };
    const sowWords = coerceToString(a.sourceOfWealth).trim().split(/\s+/).filter(w => w.length > 0);
    if (sowWords.length > 0 && sowWords.length < 4) return { pass: false, warning: 'Source of wealth must contain at least 4 words', field: 'sourceOfWealth', severity: QualitySeverity.CRITICAL };
    return { pass: true };
  },
  sanctionedCountry: (a, ctx) => {
    const found = _scanForCountries(a, ctx);
    if (found.sanctioned.length > 0) {
      const names = found.sanctioned.map(c => `${c} (${getCountryName(c)})`).join(', ');
      return { pass: false, warning: `Sanctioned country detected: ${names}. Customer cannot be onboarded without Compliance approval.`, field: 'sanctionedCountry', severity: QualitySeverity.CRITICAL, recommendationHint: 'PROHIBITED' };
    }
    if (found.highRisk.length > 0) {
      const names = found.highRisk.map(c => `${c} (${getCountryName(c)})`).join(', ');
      return { pass: false, warning: `High-risk jurisdiction detected: ${names}. Enhanced due diligence required. Document rationale in concerns/mitigants.`, field: 'sanctionedCountry', severity: QualitySeverity.WARNING };
    }
    return { pass: true };
  },
  highRiskIndustry: (a, ctx) => {
    const sicCodes = ctx?.sicCodes || [];
    for (const sic of sicCodes) {
      const desc = HIGH_RISK_SIC_CODES[String(sic).trim()];
      if (desc) {
        return { pass: false, warning: `High-risk industry: ${desc} (SIC ${sic}). Mandatory: document business rationale and risk mitigants.`, field: 'highRiskIndustry', severity: QualitySeverity.WARNING, riskHint: 'MEDIUM' };
      }
    }
    return { pass: true };
  },
};

// Scan assessment text, customer address, and individuals' nationalities for country references
function _scanForCountries(assessment, ctx) {
  const sanctioned = new Set();
  const highRisk = new Set();

  // Check individual nationalities
  const individuals = ctx?.individuals || [];
  for (const ind of individuals) {
    const nat = ind.nationality?.toUpperCase();
    if (nat && SANCTIONED_COUNTRIES.has(nat)) sanctioned.add(nat);
    else if (nat && HIGH_RISK_COUNTRIES.has(nat)) highRisk.add(nat);
  }

  // Check registered address
  const addr = coerceToString(ctx?.registeredAddress).toUpperCase();

  // Check assessment text fields
  const textFields = [
    coerceToString(assessment.businessUnderstanding),
    coerceToString(assessment.sourceOfWealth),
    addr,
  ];

  for (const text of textFields) {
    if (!text) continue;
    // Scan for 2-letter codes (word boundaries)
    const codeMatches = text.match(/\b[A-Z]{2}\b/g) || [];
    for (const m of codeMatches) {
      const upper = m.toUpperCase();
      if (SANCTIONED_COUNTRIES.has(upper)) sanctioned.add(upper);
      else if (HIGH_RISK_COUNTRIES.has(upper)) highRisk.add(upper);
    }
    // Scan for country names and aliases
    const upperText = text.toUpperCase();
    const allNames = [
      ...Array.from(SANCTIONED_COUNTRIES).map(c => [getCountryName(c), c]),
      ...Array.from(HIGH_RISK_COUNTRIES).map(c => [getCountryName(c), c]),
      ...Object.entries(COUNTRY_ALIASES).filter(([, code]) => SANCTIONED_COUNTRIES.has(code) || HIGH_RISK_COUNTRIES.has(code)),
    ];
    for (const [name, code] of allNames) {
      if (name && upperText.includes(name.toUpperCase())) {
        if (SANCTIONED_COUNTRIES.has(code)) sanctioned.add(code);
        else if (HIGH_RISK_COUNTRIES.has(code)) highRisk.add(code);
      }
    }
  }

  return { sanctioned: Array.from(sanctioned), highRisk: Array.from(highRisk) };
}

function runQualityGate(assessment, context = {}) {
  const results = [];
  for (const [name, check] of Object.entries(QualityChecks)) {
    const r = check(assessment, context);
    if (!r.pass) results.push({ check: name, ...r });
  }
  const criticals = results.filter(r => r.severity === QualitySeverity.CRITICAL);
  const warnings = results.filter(r => r.severity === QualitySeverity.WARNING);
  const infos = results.filter(r => r.severity === QualitySeverity.INFO);
  return { pass: criticals.length === 0, results, criticals, warnings, infos, requiresOverride: criticals.length > 0 };
}


// ============================================================
// LAYER 2: EXPECTED ACTIVITY EXTRACTION
// ============================================================

function extractActivityProfile(text) {
  text = coerceToString(text).trim();
  if (!text) return null;

  const profile = { monthlyInboundValue: null, monthlyOutboundValue: null, monthlyInboundCount: null, monthlyOutboundCount: null, monthlyTurnover: null, primarySchemes: [], rawText: text, extractedAt: new Date().toISOString(), confidence: 'LOW' };

  // Turnover
  const turnoverMatch = text.match(/[£$€](\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\d+)\s*(k|m)?\s*(monthly|per month|p\.?m\.?|monthly turnover|turnover)/i);
  if (turnoverMatch) {
    let v = parseFloat(turnoverMatch[1].replace(/,/g, ''));
    if ((turnoverMatch[2] || '').toLowerCase() === 'k') v *= 1000;
    if ((turnoverMatch[2] || '').toLowerCase() === 'm') v *= 1000000;
    profile.monthlyTurnover = v;
  }

  // Inbound value
  const inMatch = text.match(/[£$€](\d{1,3}(?:,\d{3})*|\d+)\s*(k|m)?\s*(monthly\s+)?inbound/i) || text.match(/inbound[^.]*?[£$€](\d{1,3}(?:,\d{3})*|\d+)\s*(k|m)?/i);
  if (inMatch) { let v = parseFloat(inMatch[1].replace(/,/g, '')); if ((inMatch[2] || '').toLowerCase() === 'k') v *= 1000; if ((inMatch[2] || '').toLowerCase() === 'm') v *= 1000000; profile.monthlyInboundValue = v; }

  // Outbound value
  const outMatch = text.match(/[£$€](\d{1,3}(?:,\d{3})*|\d+)\s*(k|m)?\s*(monthly\s+)?outbound/i) || text.match(/outbound[^.]*?[£$€](\d{1,3}(?:,\d{3})*|\d+)\s*(k|m)?/i);
  if (outMatch) { let v = parseFloat(outMatch[1].replace(/,/g, '')); if ((outMatch[2] || '').toLowerCase() === 'k') v *= 1000; if ((outMatch[2] || '').toLowerCase() === 'm') v *= 1000000; profile.monthlyOutboundValue = v; }

  // Outbound count
  const outCountPatterns = [/(\d+)\s*(outbound|outgoing)\s*(payment|transaction|transfer)s?\s*(per month|monthly|\/month|p\.?m\.?)/i, /~?(\d+)\s*(outbound|outgoing)\s*(fps|chaps|bacs|swift)?/i, /(approximately|approx|~|about|around)\s*(\d+)\s*(outbound|outgoing)/i];
  for (const p of outCountPatterns) { const m = text.match(p); if (m) { profile.monthlyOutboundCount = parseInt(m[1] || m[2]); break; } }

  // Inbound count
  const inCountPatterns = [/(\d+)\s*(inbound|incoming)\s*(payment|transaction|transfer)s?\s*(per month|monthly)/i, /~?(\d+)\s*(inbound|incoming)/i];
  for (const p of inCountPatterns) { const m = text.match(p); if (m) { profile.monthlyInboundCount = parseInt(m[1]); break; } }

  // Schemes
  if (/fps|faster payment/i.test(text)) profile.primarySchemes.push('FPS');
  if (/chaps/i.test(text)) profile.primarySchemes.push('CHAPS');
  if (/bacs/i.test(text)) profile.primarySchemes.push('BACS');
  if (/swift|international/i.test(text)) profile.primarySchemes.push('SWIFT');

  // Infer from turnover
  if (profile.monthlyTurnover && !profile.monthlyInboundValue && !profile.monthlyOutboundValue) {
    profile.monthlyInboundValue = profile.monthlyTurnover;
    profile.monthlyOutboundValue = Math.round(profile.monthlyTurnover * 0.7);
  }

  // Confidence
  let conf = 0;
  if (profile.monthlyTurnover || profile.monthlyInboundValue || profile.monthlyOutboundValue) conf++;
  if (profile.monthlyInboundCount || profile.monthlyOutboundCount) conf++;
  if (profile.primarySchemes.length > 0) conf++;
  profile.confidence = conf >= 3 ? 'HIGH' : conf >= 2 ? 'MEDIUM' : conf >= 1 ? 'LOW' : 'NONE';

  return profile;
}


// ============================================================
// LAYER 3: MONITORING ENGINE
// ============================================================

const AlertType = { VOLUME_HIGH: 'VOLUME_HIGH', VOLUME_LOW: 'VOLUME_LOW', VALUE_HIGH: 'VALUE_HIGH', VALUE_LOW: 'VALUE_LOW', SCHEME_DEVIATION: 'SCHEME_DEVIATION', VELOCITY: 'VELOCITY', DORMANCY: 'DORMANCY', LARGE_SINGLE: 'LARGE_SINGLE' };
const AlertSeverity = { LOW: 'LOW', MEDIUM: 'MEDIUM', HIGH: 'HIGH', CRITICAL: 'CRITICAL' };
const AlertStatus = { OPEN: 'OPEN', UNDER_REVIEW: 'UNDER_REVIEW', CLOSED_FALSE_POSITIVE: 'CLOSED_FALSE_POSITIVE', CLOSED_GENUINE: 'CLOSED_GENUINE', ESCALATED: 'ESCALATED' };

const MONITORING_THRESHOLDS = {
  HIGH: { valueDeviationPct: 0.20, volumeDeviationPct: 0.30, dormancyDays: 14, velocityWindowHours: 24, velocityThresholdPct: 0.50, largeSinglePct: 0.25 },
  MEDIUM: { valueDeviationPct: 0.35, volumeDeviationPct: 0.50, dormancyDays: 30, velocityWindowHours: 24, velocityThresholdPct: 0.60, largeSinglePct: 0.35 },
  LOW: { valueDeviationPct: 0.50, volumeDeviationPct: 0.75, dormancyDays: 60, velocityWindowHours: 24, velocityThresholdPct: 0.70, largeSinglePct: 0.50 },
};

class MonitoringEngine {
  constructor(eventBus, ledger, options = {}) {
    this.profiles = new Map();
    this.alerts = [];
    this.actuals = new Map();
    this.eventBus = eventBus || null;
    this.ledger = ledger || null;
    this._businessDate = options.businessDate || _defaultBusinessDate;

    // Auto-subscribe to events if EventBus provided
    if (this.eventBus && typeof this.eventBus.subscribe === 'function') {
      this._subscribeToEvents();
    }
  }

  _nowISO() { return this._businessDate.getCurrentDate().toISOString(); }

  setLedger(ledger) {
    this.ledger = ledger;
  }

  _subscribeToEvents() {
    this.eventBus.subscribe('PaymentSettled', (event) => {
      try {
        this._handlePaymentSettled(event);
      } catch (e) {
        console.error('MonitoringEngine: error handling PaymentSettled:', e.message);
      }
    });

    this.eventBus.subscribe('TransactionPosted', (event) => {
      try {
        this._handleTransactionPosted(event);
      } catch (e) {
        // Silent — not all transactions are customer-facing
      }
    });
  }

  _handlePaymentSettled(event) {
    const data = event.data || {};
    const { direction, amount, scheme } = data;
    if (!amount || !direction) return;

    const customerId = this._resolveCustomerId(event);
    if (!customerId) return;

    if (!this.profiles.has(customerId)) return;

    this.recordTransaction(customerId, {
      amount,
      direction: direction.toUpperCase(),
      scheme: scheme || 'UNKNOWN',
      counterparty: data.beneficiaryName || data.senderName || null,
      timestamp: event.timestamp || this._nowISO(),
    });
  }

  _handleTransactionPosted(event) {
    const data = event.data || {};
    if (!data.customerId || !data.amount) return;
    if (!this.profiles.has(data.customerId)) return;

    if (data.metadata?.internal) return;

    this.recordTransaction(data.customerId, {
      amount: data.amount,
      direction: data.direction || 'UNKNOWN',
      scheme: data.scheme || 'LEDGER',
      timestamp: event.timestamp || this._nowISO(),
    });
  }

  _resolveCustomerId(event) {
    const data = event.data || {};

    if (data.customerId) return data.customerId;

    if (this.ledger && data.accountId) {
      try {
        const account = this.ledger.getAccount(data.accountId);
        return account?.customerId || null;
      } catch { return null; }
    }

    if (this.ledger && data.sourceAccountId) {
      try {
        const account = this.ledger.getAccount(data.sourceAccountId);
        return account?.customerId || null;
      } catch { return null; }
    }

    return null;
  }

  registerProfile(customerId, profile, riskRating) {
    const sanitized = {
      ...profile,
      monthlyInboundCount: profile.monthlyInboundCount ?? 0,
      monthlyOutboundCount: profile.monthlyOutboundCount ?? 0,
      monthlyInboundValue: profile.monthlyInboundValue ?? 0,
      monthlyOutboundValue: profile.monthlyOutboundValue ?? 0,
      riskRating,
      registeredAt: this._nowISO(),
    };
    this.profiles.set(customerId, sanitized);
    if (!this.actuals.has(customerId)) this.actuals.set(customerId, { transactions: [], periodSummaries: [] });
  }

  getProfile(customerId) { return this.profiles.get(customerId); }

  recordTransaction(customerId, { amount, direction, scheme, counterparty, timestamp }) {
    requireFinitePositiveNumber(amount, 'Transaction amount');
    if (!this.actuals.has(customerId)) this.actuals.set(customerId, { transactions: [], periodSummaries: [] });
    this.actuals.get(customerId).transactions.push({ id: genId('txn_'), amount, direction, scheme, counterparty, timestamp: timestamp || this._nowISO() });
  }

  runMonitoring(customerId, periodStart, periodEnd) {
    const profile = this.profiles.get(customerId);
    if (!profile) return { alerts: [], error: 'No profile' };
    const actuals = this.actuals.get(customerId);
    if (!actuals) return { alerts: [], error: 'No data' };
    const thresholds = MONITORING_THRESHOLDS[profile.riskRating] || MONITORING_THRESHOLDS.MEDIUM;
    const periodTxns = actuals.transactions.filter(t => { const ts = new Date(t.timestamp); return ts >= periodStart && ts <= periodEnd; });
    const inbound = periodTxns.filter(t => t.direction === 'INBOUND');
    const outbound = periodTxns.filter(t => t.direction === 'OUTBOUND');
    const totalInbound = inbound.reduce((s, t) => s + t.amount, 0);
    const totalOutbound = outbound.reduce((s, t) => s + t.amount, 0);
    const newAlerts = [];
    const createAlert = (type, severity, details) => { const a = { id: genId('alert_'), customerId, type, severity, status: AlertStatus.OPEN, details, periodStart: periodStart.toISOString(), periodEnd: periodEnd.toISOString(), createdAt: this._nowISO(), reviewedBy: null, reviewNotes: null }; newAlerts.push(a); this.alerts.push(a); };

    // Value deviation — inbound
    if (profile.monthlyInboundValue) {
      const dev = (totalInbound - profile.monthlyInboundValue) / profile.monthlyInboundValue;
      if (dev > thresholds.valueDeviationPct) createAlert(AlertType.VALUE_HIGH, dev > thresholds.valueDeviationPct * 2 ? AlertSeverity.HIGH : AlertSeverity.MEDIUM, { metric: 'inbound_value', expected: profile.monthlyInboundValue, actual: totalInbound, deviationPct: Math.round(dev * 100) });
      if (dev < -thresholds.valueDeviationPct && totalInbound > 0) createAlert(AlertType.VALUE_LOW, AlertSeverity.LOW, { metric: 'inbound_value', expected: profile.monthlyInboundValue, actual: totalInbound, deviationPct: Math.round(dev * 100) });
    }

    // Value deviation — outbound
    if (profile.monthlyOutboundValue) {
      const dev = (totalOutbound - profile.monthlyOutboundValue) / profile.monthlyOutboundValue;
      if (dev > thresholds.valueDeviationPct) createAlert(AlertType.VALUE_HIGH, dev > thresholds.valueDeviationPct * 2 ? AlertSeverity.HIGH : AlertSeverity.MEDIUM, { metric: 'outbound_value', expected: profile.monthlyOutboundValue, actual: totalOutbound, deviationPct: Math.round(dev * 100) });
    }

    // Volume deviation — outbound count
    if (profile.monthlyOutboundCount) {
      const dev = (outbound.length - profile.monthlyOutboundCount) / profile.monthlyOutboundCount;
      if (Math.abs(dev) > thresholds.volumeDeviationPct) createAlert(dev > 0 ? AlertType.VOLUME_HIGH : AlertType.VOLUME_LOW, Math.abs(dev) > thresholds.volumeDeviationPct * 2 ? AlertSeverity.HIGH : AlertSeverity.MEDIUM, { metric: 'outbound_count', expected: profile.monthlyOutboundCount, actual: outbound.length, deviationPct: Math.round(dev * 100) });
    }

    // Scheme deviation
    if (profile.primarySchemes?.length > 0 && periodTxns.length > 0) {
      const schemeCount = {}; for (const t of periodTxns) schemeCount[t.scheme] = (schemeCount[t.scheme] || 0) + 1;
      for (const [scheme, count] of Object.entries(schemeCount)) {
        if (!profile.primarySchemes.includes(scheme) && count / periodTxns.length > 0.20)
          createAlert(AlertType.SCHEME_DEVIATION, AlertSeverity.MEDIUM, { unexpectedScheme: scheme, pctOfTotal: Math.round(count / periodTxns.length * 100), expectedSchemes: profile.primarySchemes });
      }
    }

    // Velocity
    if (inbound.length > 0 && outbound.length > 0 && totalInbound > 0) {
      const sortedIn = [...inbound].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      const sortedOut = [...outbound].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      for (const inTxn of sortedIn) {
        const inTime = new Date(inTxn.timestamp).getTime();
        const windowEnd = inTime + thresholds.velocityWindowHours * 3600000;
        const outInWindow = sortedOut.filter(o => { const ot = new Date(o.timestamp).getTime(); return ot >= inTime && ot <= windowEnd; });
        const outVal = outInWindow.reduce((s, o) => s + o.amount, 0);
        if (outVal >= inTxn.amount * thresholds.velocityThresholdPct && inTxn.amount >= 10000) {
          createAlert(AlertType.VELOCITY, AlertSeverity.HIGH, { inboundAmount: inTxn.amount, outboundWithinWindow: outVal, windowHours: thresholds.velocityWindowHours });
          break;
        }
      }
    }

    // Dormancy
    if (periodTxns.length === 0) {
      const lastTxn = actuals.transactions.filter(t => new Date(t.timestamp) < periodStart).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
      if (lastTxn) {
        const daysSince = Math.floor((periodStart - new Date(lastTxn.timestamp)) / 86400000);
        if (daysSince >= thresholds.dormancyDays) createAlert(AlertType.DORMANCY, AlertSeverity.MEDIUM, { daysSinceLastTransaction: daysSince, threshold: thresholds.dormancyDays });
      }
    }

    // Large single
    const expectedMonthly = profile.monthlyInboundValue || profile.monthlyTurnover || 0;
    if (expectedMonthly > 0) {
      for (const t of periodTxns) {
        if (t.amount >= expectedMonthly * thresholds.largeSinglePct) {
          createAlert(AlertType.LARGE_SINGLE, t.amount >= expectedMonthly ? AlertSeverity.HIGH : AlertSeverity.MEDIUM, { amount: t.amount, direction: t.direction, pctOfMonthly: Math.round(t.amount / expectedMonthly * 100) });
          break;
        }
      }
    }

    actuals.periodSummaries.push({ periodStart: periodStart.toISOString(), periodEnd: periodEnd.toISOString(), inboundCount: inbound.length, outboundCount: outbound.length, inboundValue: totalInbound, outboundValue: totalOutbound, alertCount: newAlerts.length });
    return { alerts: newAlerts, summary: { inboundCount: inbound.length, outboundCount: outbound.length, inboundValue: totalInbound, outboundValue: totalOutbound, transactionCount: periodTxns.length } };
  }

  reviewAlert(alertId, { reviewedBy, status, notes }) {
    const a = this.alerts.find(a => a.id === alertId); if (!a) throw new Error('Not found');
    a.status = status; a.reviewedBy = reviewedBy; a.reviewNotes = notes; a.reviewedAt = this._nowISO(); return a;
  }

  getAlerts(customerId, statusFilter) { let a = this.alerts.filter(a => a.customerId === customerId); if (statusFilter) a = a.filter(x => x.status === statusFilter); return a; }
  getAllOpenAlerts() { return this.alerts.filter(a => a.status === AlertStatus.OPEN); }
  getMonitoringSummary(customerId) { const p = this.profiles.get(customerId); const a = this.actuals.get(customerId); const alerts = this.getAlerts(customerId); return { profile: p, totalTransactions: a?.transactions.length || 0, periodSummaries: a?.periodSummaries || [], alertCounts: { open: alerts.filter(a => a.status === AlertStatus.OPEN).length, total: alerts.length, escalated: alerts.filter(a => a.status === AlertStatus.ESCALATED).length } }; }
}

// Export for testing
export { QualitySeverity, QualityChecks, runQualityGate, extractActivityProfile, AlertType, AlertSeverity, AlertStatus, MonitoringEngine, MONITORING_THRESHOLDS, SANCTIONED_COUNTRIES, HIGH_RISK_COUNTRIES, HIGH_RISK_SIC_CODES };
